import { useCallback, useMemo } from "react";
import {
  TENANT_FEATURE_REALMS,
  TenantFeature,
} from "@/constants/tenant-feature-constant";
import { getStoredRealm } from "@/utils/auth-storage";

export interface TenantGated {
  feature?: TenantFeature;
}

export interface UseTenantFeaturesResult {
  realm: string | undefined;
  isEnabled: (feature: TenantFeature) => boolean;
  filterByFeature: <T extends TenantGated>(items: readonly T[]) => T[];
  pickExclusive: <T extends TenantGated>(
    items: readonly T[],
    feature: TenantFeature
  ) => T[];
}

export const useTenantFeatures = (): UseTenantFeaturesResult => {
  const realm = getStoredRealm() ?? undefined;

  const isEnabled = useCallback(
    (feature: TenantFeature) =>
      !!realm && TENANT_FEATURE_REALMS[feature].has(realm),
    [realm]
  );

  const filterByFeature = useCallback(
    <T extends TenantGated>(items: readonly T[]) =>
      items.filter(({ feature }) => !feature || isEnabled(feature)),
    [isEnabled]
  );

  /** A tenant with the feature sees only its items; everyone else sees the untagged ones. */
  const pickExclusive = useCallback(
    <T extends TenantGated>(items: readonly T[], feature: TenantFeature) =>
      isEnabled(feature)
        ? items.filter((item) => item.feature === feature)
        : filterByFeature(items),
    [isEnabled, filterByFeature]
  );

  return useMemo(
    () => ({ realm, isEnabled, filterByFeature, pickExclusive }),
    [realm, isEnabled, filterByFeature, pickExclusive]
  );
};
